import React, { useState, useEffect } from "react";
import { Plus, Trash2, Eye, ShoppingCart, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import QuotationModal from "./QuotationModal";

const CheckoutForm = ({ onClose }) => {
  const existingCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
  const initialFormData = {
    name: existingCartItems[0]?.name || "",
    mobile: existingCartItems[0]?.mobile || "",
    address: existingCartItems[0]?.address || "",
    pincode: existingCartItems[0]?.pincode || "",
    selectedLocation: existingCartItems[0]?.selectedLocation || "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [pincodeData, setPincodeData] = useState([]);
  const businessPhoneNumber = "+91 99988 52256";

  // SEO and accessibility improvements
  useEffect(() => {
    // Set page title for SEO
    document.title = "Checkout - Complete Your Order | NaturalX";

    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.";
      document.head.appendChild(meta);
    }

    // Focus management for accessibility
    const firstInput = document.querySelector('input[name="name"]');
    if (firstInput) {
      firstInput.focus();
    }

    // Cleanup function
    return () => {
      document.title = "NaturalX";
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleWhatsAppClick = () => {
    const cartItemsDetails = existingCartItems
      .map((item, index) => {
        return `(${index + 1}) Product: ${item.productName}, Pattern: ${
          item.productsPattern || "Standard"
        }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
          item.Area
        }, Discount: ${item.discount}%, Effective Rate: ₹${
          item.effectiveRate
        }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
          item.amountWithIgst || 0,
        ).toFixed(2)} (incl. taxes)`;
      })
      .join("\n");

    const total = existingCartItems.reduce(
      (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
      0,
    );

    const whatsappMessage = `Hello, I would like to proceed with my order.\n\nOrder Details:\n${cartItemsDetails}\n\nCustomer Details:\nName: ${
      formData.name
    }\nMobile: ${formData.mobile}\nAddress: ${formData.address}\nPincode: ${
      formData.pincode
    }\nLocation: ${formData.selectedLocation}\n\nTotal Amount: ₹${total.toFixed(
      2,
    )}`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
      whatsappMessage,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const fetchPincodeData = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`,
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice);
        } else {
          setPincodeData([]);
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeData([]);
      }
    } else {
      setPincodeData([]);
    }
  };

  useEffect(() => {
    if (formData.pincode.length === 6) {
      fetchPincodeData(formData.pincode);
    }
  }, [formData.pincode]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      valid = false;
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
      valid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      valid = false;
    } else if (formData.address.trim().length < 10) {
      newErrors.address = "Please provide a complete address";
      valid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      valid = false;
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        const cartItemsDetails = existingCartItems
          .map((item, index) => {
            return `(${index + 1}) Product: ${item.productName}, Pattern: ${
              item.productsPattern || "Standard"
            }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
              item.Area
            }, Discount: ${item.discount}%, Effective Rate: ₹${
              item.effectiveRate
            }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
              item.amountWithIgst || 0,
            ).toFixed(2)} (incl. taxes)`;
          })
          .join("\n");

        const total = existingCartItems.reduce(
          (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
          0,
        );

        const message = `New order from ${formData.name}. Address: ${
          formData.address
        }, Pincode: ${formData.pincode}, Location: ${
          formData.selectedLocation
        }\n\nOrder Details:\n${cartItemsDetails}\n\nTotal Amount: ₹${total.toFixed(
          2,
        )}`;

        const response = await fetch(
          `https://webadmin.grapits.in/api/save_contact?name=${encodeURIComponent(
            formData.name,
          )}&phone=${encodeURIComponent(
            formData.mobile,
          )}&email=${encodeURIComponent("-")}&msg=${encodeURIComponent(
            message,
          )}`,
        );

        if (!response.ok) {
          throw new Error("Failed to save contact");
        }

        console.log("Form submitted and contact saved:", formData);
        setSubmitSuccess(true);

        // Delay WhatsApp redirect to show success message
        setTimeout(() => {
          handleWhatsAppClick();
          onClose();
          localStorage.removeItem("cartItems");

          // Refresh the page
          window.location.reload();
        }, 1500);
      } catch (error) {
        console.error("Error saving contact:", error);
        setErrors({ submit: "Failed to submit order. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle escape key for accessibility
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-labelledby="checkout-title"
      aria-describedby="checkout-description"
    >
      <div className="relative w-full max-w-md mx-auto bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2
              id="checkout-title"
              className="text-xl font-semibold text-gray-900 sm:text-2xl"
            >
              Complete Your Cart
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
              aria-label="Close checkout form"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p id="checkout-description" className="mt-2 text-sm text-gray-600">
            Enter your details to proceed with WhatsApp order confirmation
          </p>
        </div>

        {/* Form Content */}
        <div className="px-6 py-4">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Order Submitted Successfully!
              </h3>
              <p className="text-sm text-gray-600">
                Redirecting to WhatsApp...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your full name"
                  required
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p
                    id="name-error"
                    className="mt-2 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Mobile Field */}
              <div>
                <label
                  htmlFor="mobile"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.mobile ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  required
                  aria-describedby={errors.mobile ? "mobile-error" : undefined}
                />
                {errors.mobile && (
                  <p
                    id="mobile-error"
                    className="mt-2 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.mobile}
                  </p>
                )}
              </div>

              {/* Address Field */}
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Complete Address *
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors resize-none ${
                    errors.address ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your complete address including landmark"
                  rows="3"
                  required
                  aria-describedby={
                    errors.address ? "address-error" : undefined
                  }
                />
                {errors.address && (
                  <p
                    id="address-error"
                    className="mt-2 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.address}
                  </p>
                )}
              </div>

              {/* Pincode Field */}
              <div>
                <label
                  htmlFor="pincode"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Pincode *
                </label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.pincode ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter 6-digit pincode"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  required
                  aria-describedby={
                    errors.pincode ? "pincode-error" : undefined
                  }
                />
                {errors.pincode && (
                  <p
                    id="pincode-error"
                    className="mt-2 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.pincode}
                  </p>
                )}
              </div>

              {/* Location Dropdown */}
              {formData.pincode.length === 6 && pincodeData.length > 0 && (
                <div>
                  <label
                    htmlFor="selectedLocation"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Select Location
                  </label>
                  <select
                    id="selectedLocation"
                    name="selectedLocation"
                    value={formData.selectedLocation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    aria-describedby="location-help"
                  >
                    <option value="">Select your location</option>
                    {pincodeData.map((location, index) => (
                      <option key={index} value={location.Name}>
                        {location.Name}
                      </option>
                    ))}
                  </select>
                  <p id="location-help" className="mt-2 text-xs text-gray-500">
                    Select the most accurate location for delivery
                  </p>
                </div>
              )}

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600" role="alert">
                    {errors.submit}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full sm:flex-1 px-6 py-3 text-white text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isLoading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Submit Order"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <div className="flex items-center text-xs text-gray-500">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Your information is secure and will be used only for order
            processing
          </div>
        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  console.log("🚀 ~ CartPage ~ cartItems:", cartItems);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Shopping Cart - Your Interior Wall Textures Store";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.";
      document.head.appendChild(meta);
    }

    try {
      const existingCartItems =
        JSON.parse(localStorage.getItem("cartItems")) || [];
      setCartItems(existingCartItems);

      const total = existingCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);
    } catch (error) {
      console.error("Error loading cart items:", error);
      setCartItems([]);
      setTotalAmount(0);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (showCheckout || showQuotationModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to reset the overflow when the component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCheckout, showQuotationModal]);

  const handleRemoveItem = async (index) => {
    if (isRemoving) return;
    setIsRemoving(true);

    try {
      const updatedCartItems = [...cartItems];
      updatedCartItems.splice(index, 1);
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
      setCartItems(updatedCartItems);

      const total = updatedCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);

      await new Promise((resolve) => setTimeout(resolve, 500));
      window.location.reload();
    } catch (error) {
      console.error("Error removing item from cart:", error);
      alert("Failed to remove item from cart. Please try again.");
      setIsRemoving(false);
    }
  };

  const handleAddMoreProducts = () => {
    navigate("/Products/5/Interior%20Wall%20Textures");
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add products before checkout.");
      return;
    }
    setShowCheckout(true);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ShoppingCart",
    name: "Shopping Cart",
    description: "Interior wall texture products cart",
    offers: cartItems.map((item) => ({
      "@type": "Offer",
      name: item.productName,
      price: item.amountWithIgst,
      priceCurrency: "INR",
    })),
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <main className="min-h-screen bg-gray-50 py-4 mt-12 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700 mr-2 sm:mr-3" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                  Cart Section
                </h1>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">
                  {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                </p>
              </div>
            </div>
          </header>
          {cartItems.length === 0 ? (
            <section className="text-center py-12 sm:py-16">
              <div className="max-w-md mx-auto">
                <ShoppingCart className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400 mx-auto mb-6" />
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  Your cart is empty
                </h2>
                <p className="text-gray-600 mb-8">
                  Looks like you haven't added any interior wall textures yet.
                  Discover our premium collection and transform your space.
                </p>
                <button
                  onClick={handleAddMoreProducts}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Browse Products
                </button>
              </div>
            </section>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <section className="lg:col-span-2">
                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Items in your cart
                    </h2>
                  </div>
                  <ul className="divide-y divide-gray-200" role="list">
                    {cartItems.map((item, index) => (
                      <li
                        key={index}
                        className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <article className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-shrink-0 self-center sm:self-start">
                            <img
                              src={item.mainImage}
                              alt={`${item.productName} - Interior Wall Texture`}
                              className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-gray-200"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                              <div className="flex-1">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                  {item.productName + " "}
                                  <span className="ml-1 font-medium text-gray-600">
                                    {"/ " + item.Area}
                                  </span>
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-gray-600">
                                  <div>
                                    <span className="font-medium">
                                      Pattern:
                                    </span>
                                    <span className="ml-1">
                                      {item.productsPattern || "Standard"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium">Rate:</span>
                                    <span className="ml-1">₹{item.rate}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium">Area:</span>
                                    <span className="ml-1">
                                      {item.sqft} sq ft
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Discount:
                                    </span>
                                    <span className="ml-1">
                                      {item.discount}%
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Effective Rate:
                                    </span>
                                    <span className="ml-1">
                                      ₹{item.effectiveRate}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Subtotal:
                                    </span>
                                    <span className="ml-1">₹{item.amount}</span>
                                  </div>
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                  <p className="text-lg font-semibold text-gray-900">
                                    Total: ₹
                                    {parseFloat(
                                      item.amountWithIgst || 0,
                                    ).toFixed(2)}
                                    <span className="text-sm font-normal text-gray-500 ml-1">
                                      (incl. taxes)
                                    </span>
                                  </p>
                                  <a
                                    href={item.pageUrl}
                                    className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 text-sm"
                                    aria-label={`View ${item.productName} product details`}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View Product
                                  </a>
                                </div>
                              </div>
                              <div className="flex-shrink-0 self-end sm:self-start">
                                <button
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={isRemoving}
                                  className={`p-2 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                                    isRemoving
                                      ? "text-gray-400 cursor-not-allowed"
                                      : "text-red-600 hover:text-red-900 hover:bg-red-50"
                                  }`}
                                  aria-label={`Remove ${item.productName} from cart`}
                                  title={`Remove ${item.productName} from cart`}
                                >
                                  {isRemoving ? (
                                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-300 border-t-red-600"></div>
                                  ) : (
                                    <Trash2 className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
              <aside className="lg:col-span-1">
                <div className="bg-white shadow-sm rounded-lg border border-gray-200 sticky top-4">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Order Summary
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    <div className="space-y-3 mb-6">
                      <div className="mb-4">
                        <h3 className="text-md font-medium text-gray-900 mb-2">
                          Items:
                        </h3>
                        <ul className="divide-y divide-gray-200">
                          {cartItems.map((item, index) => (
                            <li key={index} className="py-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  {item.productName} (
                                  {item.productsPattern || "Standard"})
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {parseFloat(item.amountWithIgst || 0).toFixed(
                                    2,
                                  )}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Subtotal ({cartItems.length} items)
                        </span>
                        <span className="font-medium">
                          ₹{totalAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex justify-between text-lg font-semibold text-gray-900">
                          <span>Total</span>
                          <span>₹{totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={handleProceedToCheckout}
                        className="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        Proceed to Checkout
                      </button>
                      <button
                        onClick={handleAddMoreProducts}
                        className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <Plus className="w-5 h-5 mr-2 inline" />
                        Add More Products
                      </button>
                      <button
                        onClick={() => setShowQuotationModal(true)}
                        className="w-full px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      >
                        Download Quotation
                      </button>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
        {showCheckout && (
          <CheckoutForm onClose={() => setShowCheckout(false)} />
        )}
        {showQuotationModal && (
          <QuotationModal onClose={() => setShowQuotationModal(false)} />
        )}
      </main>
    </>
  );
};

export default CartPage;
